// Using translations key as id
export const IMAGE_MAPPING = {
  SAGITTAL: "99",
  CORONAL: "100",
  CT: "102",
};

export const VALID_IMAGE_TYPES = {
  DX: "DX",
  CT: "CT",
  CR: "CR",
};

export const SPINESIM_MODALITIES = {
  DX: "DX",
  CT: "CT",
  CT_VOLUME: "CT_Volume",
  DRR: "DRR",
  MR: "MR",
  MR_VOLUME: "MR_Volume",
  POSTURE: "Posture",
  PAIN: "Pain",
  QUALITY: "Quality",
  CLINICAL_HISTORY: "ClinicalHistory",
};

export const MedicalImagesKey = {
  sagittal: "lateral",
  coronal: "frontal",
  ct: "volume",
  mr: "mri",
};

export const InstanceOrientation = {
  Sagittal: "sagittal",
  Coronal: "coronal",
};

export const IMAGE_ORIENTATION = {
  SAGITTAL: "sagittal",
  CORONAL: "coronal",
  AXIAL: "axial",
  LATERAL: "lateral",
  FRONTAL: "frontal",
};

export const IMAGE_ORIENTATION_LABELS = {
  [IMAGE_ORIENTATION.SAGITTAL]: "Sagittal",
  [IMAGE_ORIENTATION.CORONAL]: "Coronal",
  [IMAGE_ORIENTATION.AXIAL]: "Axial",
  [IMAGE_ORIENTATION.LATERAL]: "Lateral",
  [IMAGE_ORIENTATION.FRONTAL]: "Frontal",
};

export const IMAGE_ORIENTATION_ABBREVIATIONS = {
  [IMAGE_ORIENTATION.SAGITTAL]: "S",
  [IMAGE_ORIENTATION.CORONAL]: "C",
  [IMAGE_ORIENTATION.AXIAL]: "A",
  [IMAGE_ORIENTATION.LATERAL]: "L",
  [IMAGE_ORIENTATION.FRONTAL]: "F",
};

export const IMAGE_ORIENTATION_KEYWORDS = {
  [IMAGE_ORIENTATION.SAGITTAL]: ["sag", "lateral", "lat"],
  [IMAGE_ORIENTATION.CORONAL]: ["cor", "frontal", "front"],
  [IMAGE_ORIENTATION.AXIAL]: ["ax", "axial", "trans"],
  [IMAGE_ORIENTATION.LATERAL]: ["lat", "lateral"],
  [IMAGE_ORIENTATION.FRONTAL]: ["front", "frontal", "cor"],
};

export const MorphologyOrientation = {
  FRONT: "Front",
  BACK: "Back",
  LEFT: "Left",
  RIGHT: "Right",
};

// Standard order for morphology/posture images
export const MORPHOLOGY_ORIENTATION_ORDER = [
  MorphologyOrientation.FRONT,
  MorphologyOrientation.LEFT,
  MorphologyOrientation.BACK,
  MorphologyOrientation.RIGHT,
];

export const MedicalImagesType = {
  ABSENTINFO: "DX",
  LATERALDX: "LateralDX",
  FRONTDX: "FrontDX",
  SAGITTAL_CT: "SagittalCt",
  SAGITTAL_DRR_CT: "SagittalDrrCt",
  CORONAL_CT: "CoronalCt",
  CORONAL_DRR_CT: "CoronalDrrCt",
  AXIAL_RADIODENSITY_CT: "AxialRadiodensityCt",
  AXIAL_CT: "AxialCt",
  VOLUME_CT: "VolumeCt",
  SAGITTAL_DRR_VALIDATION: "SagittalDrrValidation",
  CORONAL_DRR_VALIDATION: "CoronalDrrValidation",
  SAGITTAL_MRI: "SagittalMri",
  CORONAL_MRI: "CoronalMri",
  AXIAL_MRI: "AxialMri",
  VOLUME_MRI: "VolumeMri",
  LATERAL_SURGERY_DX: "LateralSurgeryDX",
  LATERAL_SUPPORT_SURGERY: "LateralSupportSurgeryDX",
  FRONT_SURGERY_DX: "FrontSurgeryDX",
  LATERAL_VALIDATION_DX: "LateralValidationDX",
  FRONT_VALIDATION_DX: "FrontValidationDX",
};

export const ThumbnailEnum = {
  CT: [
    {
      srcSuffix: "_thumbnail_coronal.png",
      medicalImageType: MedicalImagesType.CORONAL,
    },
    {
      srcSuffix: "_thumbnail_sagittal.png",
      medicalImageType: MedicalImagesType.SAGITTAL,
    },
    {
      srcSuffix: "_thumbnail_volume.png",
      medicalImageType: MedicalImagesType.VOLUME,
    },
    {
      srcSuffix: "_thumbnail_axial.png",
      medicalImageType: MedicalImagesType.AXIAL,
    },
  ],
  DX_sagittal_visualize: [
    {
      srcSuffix: "_thumbnail.png",
      medicalImageType: MedicalImagesType.LATERALDX,
    },
  ],
  DX_sagittal_analyse: [
    {
      srcSuffix: "_thumbnail.png",
      medicalImageType: MedicalImagesType.LATERALDX,
    },
  ],
  DX_sagittal_align: [
    {
      srcSuffix: "_thumbnail.png",
      medicalImageType: MedicalImagesType.LATERAL_SURGERY_DX,
    },
  ],
  DX_coronal_visualize: [
    {
      srcSuffix: "_thumbnail.png",
      medicalImageType: MedicalImagesType.FRONTDX,
    },
  ],
  DX_coronal_analyse: [
    {
      srcSuffix: "_thumbnail.png",
      medicalImageType: MedicalImagesType.FRONTDX,
    },
  ],
  DX_coronal_align: [
    {
      srcSuffix: "_thumbnail.png",
      medicalImageType: MedicalImagesType.FRONT_SURGERY_DX,
    },
  ],
};

/**
 * Standard DICOM modality codes
 * https://dicom.nema.org/medical/dicom/current/output/chtml/part16/sect_CID_29.html
 */
export const MODALITY = {
  // Common modalities
  CT: "CT", // Computed Tomography
  MR: "MR", // Magnetic Resonance
  DX: "DX", // Digital Radiography
  US: "US", // Ultrasound
  PT: "PT", // Positron Emission Tomography
  CR: "CR", // Computed Radiography
  NM: "NM", // Nuclear Medicine

  // Less common modalities
  RF: "RF", // Radio Fluoroscopy
  MG: "MG", // Mammography
  XA: "XA", // X-Ray Angiography
  OT: "OT", // Other

  // Custom
  MO: "MO", // Morphology
};

/**
 * Mapping from custom or legacy modality values to standard DICOM codes
 */
export const MODALITY_MAPPING = {
  // Map common variations to standard codes
  "X-ray": MODALITY.DX,
  "X-Ray": MODALITY.DX,
  xray: MODALITY.DX,
  XRay: MODALITY.DX,

  MRI: MODALITY.MR,
  mri: MODALITY.MR,

  "CT Scan": MODALITY.CT,
  CAT: MODALITY.CT,
  cat: MODALITY.CT,

  PET: MODALITY.PT,
  pet: MODALITY.PT,

  Ultrasound: MODALITY.US,
  ultrasound: MODALITY.US,

  // Default cases
  Unknown: MODALITY.OT,
};

/**
 * Human-readable labels for modalities
 */
export const MODALITY_LABELS = {
  [MODALITY.CT]: "CT",
  [MODALITY.MR]: "MR",
  [MODALITY.DX]: "DX",
  [MODALITY.CR]: "CR",
  [MODALITY.MO]: "Morphology",
};

export const TOMOGRAPHIC_MODALITIES = [MODALITY.CT, MODALITY.MR];

export const MEDICAL_IMAGE_THUMBNAIL_DISPLAY_MODE = {
  DEFAULT: "default",
  ANNEX: "annex",
};

export const MODALITY_TO_DICOM_LABEL = {
  CT: MODALITY.CT,
  MRI: MODALITY.MR,
  "X-ray": MODALITY.DX,
  CR: MODALITY.DX,
  Morphology: MODALITY.MO,
};

/**
 * Standardizes any modality value to a consistent DICOM code
 * @param {string} modality - The modality value to standardize
 * @returns {string} - The standardized DICOM modality code
 */
export const standardizeModality = modality => {
  if (!modality) return null;

  if (MODALITY_MAPPING[modality]) {
    return MODALITY_MAPPING[modality];
  }

  // If it's already a standard code, return it
  if (Object.values(MODALITY).includes(modality)) {
    return modality;
  }

  // If it's not mapped, check for substring matches (case insensitive)
  const lowerModality = modality.toLowerCase();
  if (lowerModality.includes("ct")) return MODALITY.CT;
  if (lowerModality.includes("mr") || lowerModality.includes("mri"))
    return MODALITY.MR;
  if (
    lowerModality.includes("xr") ||
    lowerModality.includes("x-ray") ||
    lowerModality.includes("dx")
  )
    return MODALITY.DX;

  return MODALITY.OT;
};

export const mapOrientationOptionsDxToMorphology = {
  SagittalAP: MorphologyOrientation.LEFT,
  CoronalLR: MorphologyOrientation.BACK,
  SagittalPA: MorphologyOrientation.RIGHT,
  CoronalRL: MorphologyOrientation.FRONT,
};

export const morphologyDirections = [
  "Sagittal AP",
  "Sagittal PA",
  "Coronal LR",
  "Coronal RL",
];

export const MAP_LEFT_LABELS_TO_PATIENT_POSITION_LETTER = {
  R: "L",
  L: "R",
  I: "H",
  S: "F",
  A: "P",
  P: "A",
};

export const MAP_BOTTOM_LABELS_TO_PATIENT_POSITION_LETTER = {
  I: "F",
  S: "H",
  A: "A",
  P: "P",
  L: "L",
  R: "R",
};

// DX Orientation Mappings
export const DX_PATIENT_ORIENTATION_TO_PLANE = {
  "L,F": "CORONAL",
  "R,F": "CORONAL",
  "L,H": "CORONAL",
  "R,H": "CORONAL",
  "A,F": "SAGITTAL",
  "P,F": "SAGITTAL",
  "A,H": "SAGITTAL",
  "P,H": "SAGITTAL",
};

// Helper function to map any orientation to LR/AP/IS convention
export const mapToLRAPIS = direction => {
  const mapping = {
    // Human biped mappings
    L: "L",
    R: "R",
    A: "A",
    P: "P",
    H: "S",
    F: "I", // Head->Superior, Foot->Inferior
    S: "S",
    I: "I",

    // Quadruped mappings (if needed)
    LE: "L",
    RT: "R",
    D: "P",
    V: "A", // Dorsal->Posterior, Ventral->Anterior
    CR: "S",
    CD: "I", // Cranial->Superior, Caudal->Inferior
    M: "R",
    PR: "S",
    DI: "I", // Proximal->Superior, Distal->Inferior
  };

  return mapping[direction] || direction;
};

// Helper function to get opposite direction
export const getOppositeDirection = direction => {
  const opposites = {
    L: "R",
    R: "L",
    A: "P",
    P: "A",
    S: "I",
    I: "S",
    H: "F",
    F: "H",

    // Quadruped opposites
    LE: "RT",
    RT: "LE",
    D: "V",
    V: "D",
    CR: "CD",
    CD: "CR",
    M: "L",
    PR: "DI",
    DI: "PR",
  };

  return opposites[direction] || direction;
};

export const FileCategories = {
  DICOM: "DICOM",
  NIFTI: "NIFTI",
  MHD: "MHD",
  HEIC: "HEIC",
  IMAGE: "IMAGE",
  UNKNOWN: "UNKNOWN",
};

export const DICOM_TAGS = {
  // Study/Series Date and Time Information
  STUDY_DATE: "x00080020", // (0008,0020) - Study Date
  SERIES_DATE: "x00080021", // (0008,0021) - Series Date
  ACQUISITION_DATE: "x00080022", // (0008,0022) - Acquisition Date
  STUDY_TIME: "x00080030", // (0008,0030) - Study Time
  SERIES_TIME: "x00080031", // (0008,0031) - Series Time
  ACQUISITION_TIME: "x00080032", // (0008,0032) - Acquisition Time

  // Patient Information
  PATIENT_NAME: "x00100010", // (0010,0010) - Patient's Name
  PATIENT_ID: "x00100020", // (0010,0020) - Patient ID
  PATIENT_BIRTH_DATE: "x00100030", // (0010,0030) - Patient's Birth Date
  PATIENT_SEX: "x00100040", // (0010,0040) - Patient's Sex
  PATIENT_AGE: "x00101010", // (0010,1010) - Patient's Age
  PATIENT_WEIGHT: "x00101030", // (0010,1030) - Patient's Weight
  PATIENT_SIZE: "x00101020", // (0010,1020) - Patient's Size

  // Study and Series Information
  STUDY_INSTANCE_UID: "x0020000d", // (0020,000D) - Study Instance UID
  SERIES_INSTANCE_UID: "x0020000e", // (0020,000E) - Series Instance UID
  SOP_INSTANCE_UID: "x00080018", // (0008,0018) - SOP Instance UID
  STUDY_ID: "x00200010", // (0020,0010) - Study ID
  SERIES_NUMBER: "x00200011", // (0020,0011) - Series Number
  ACQUISITION_NUMBER: "x00200012", // (0020,0012) - Acquisition Number
  INSTANCE_NUMBER: "x00200013", // (0020,0013) - Instance Number

  // Image Information
  MODALITY: "x00080060", // (0008,0060) - Modality
  IMAGE_TYPE: "x00080008", // (0008,0008) - Image Type
  IMAGE_ORIENTATION_PATIENT: "x00200037", // (0020,0037) - Image Orientation (Patient)
  IMAGE_POSITION_PATIENT: "x00200032", // (0020,0032) - Image Position (Patient)
  PATIENT_ORIENTATION: "x00200020", // (0020,0020) - Patient Orientation
  PATIENT_POSITION: "x00185100", // (0018,5100) - Patient Position
  VIEW_POSITION: "x00185101", // (0018,5101) - View Position
  SLICE_THICKNESS: "x00180050", // (0018,0050) - Slice Thickness
  SLICE_SPACING: "x00180088", // (0018,0088) - Spacing Between Slices
  PIXEL_SPACING: "x00280030", // (0028,0030) - Pixel Spacing

  // Equipment Information
  MANUFACTURER: "x00080070", // (0008,0070) - Manufacturer
  MANUFACTURER_MODEL_NAME: "x00081090", // (0008,1090) - Manufacturer's Model Name
  STATION_NAME: "x00081010", // (0008,1010) - Station Name
  SOFTWARE_VERSIONS: "x00181020", // (0018,1020) - Software Version(s)

  // Study and Series Descriptions
  STUDY_DESCRIPTION: "x00081030", // (0008,1030) - Study Description
  SERIES_DESCRIPTION: "x0008103e", // (0008,103E) - Series Description
  PROTOCOL_NAME: "x00181030", // (0018,1030) - Protocol Name
  IMAGE_COMMENTS: "x00204000", // (0020,4000) - Image Comments

  // Image Pixel Data
  SAMPLES_PER_PIXEL: "x00280002", // (0028,0002) - Samples per Pixel
  PHOTOMETRIC_INTERPRETATION: "x00280004", // (0028,0004) - Photometric Interpretation
  ROWS: "x00280010", // (0028,0010) - Rows
  COLUMNS: "x00280011", // (0028,0011) - Columns
  BITS_ALLOCATED: "x00280100", // (0028,0100) - Bits Allocated
  BITS_STORED: "x00280101", // (0028,0101) - Bits Stored
  HIGH_BIT: "x00280102", // (0028,0102) - High Bit
  PIXEL_REPRESENTATION: "x00280103", // (0028,0103) - Pixel Representation
  PIXEL_DATA: "x7fe00010", // (7FE0,0010) - Pixel Data

  // Acquisition Parameters
  KVP: "x00180060", // (0018,0060) - KVP
  EXPOSURE_TIME: "x00181150", // (0018,1150) - Exposure Time
  X_RAY_TUBE_CURRENT: "x00181151", // (0018,1151) - X-Ray Tube Current
  EXPOSURE: "x00181152", // (0018,1152) - Exposure
  FILTER_TYPE: "x00181160", // (0018,1160) - Filter Type
  FOCAL_SPOT: "x00181190", // (0018,1190) - Focal Spot(s)
  BODY_PART_EXAMINED: "x00180015", // (0018,0015) - Body Part Examined
  IMAGER_PIXEL_SPACING: "x00181164", // (0018,1164) - Imager Pixel Spacing

  // CT/MR Specific
  SLICE_LOCATION: "x00201041", // (0020,1041) - Slice Location
  WINDOW_CENTER: "x00281050", // (0028,1050) - Window Center
  WINDOW_WIDTH: "x00281051", // (0028,1051) - Window Width
  RESCALE_INTERCEPT: "x00281052", // (0028,1052) - Rescale Intercept
  RESCALE_SLOPE: "x00281053", // (0028,1053) - Rescale Slope

  // RT Structure Set (for future use)
  STRUCTURE_SET_LABEL: "x30060002", // (3006,0002) - Structure Set Label
  STRUCTURE_SET_NAME: "x30060004", // (3006,0004) - Structure Set Name
  STRUCTURE_SET_DESCRIPTION: "x30060006", // (3006,0006) - Structure Set Description
};
